import React, { useState, useEffect, useRef } from "react";
import { message, Spin } from "antd";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.js";
import { collection, addDoc, getDoc, doc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const MAX_COPIES = 20; // must match MAX_COPIES in server/routes/printing.js

// Sends the logged-in user's Firebase ID token so the server can verify who is printing
async function authHeaders() {
  const u = getAuth().currentUser;
  if (!u) throw new Error("Non connecté");
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// Server errors come back as { error, details }
function apiError(err, fallback) {
  const d = err.response?.data;
  if (err.response?.status === 401) return "Session expirée — reconnectez-vous.";
  if (typeof d?.details === "string") return d.details;
  return d?.error || fallback;
}

export default function EpsonPrintPage() {
  const auth = getAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [tab, setTab] = useState("print"); // "print" | "scan"
  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  // Print state
  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [colorMode, setColorMode] = useState("mono");
  const [copies, setCopies] = useState(1);
  const [doubleSided, setDoubleSided] = useState("none");
  const [pagesPerSheet, setPagesPerSheet] = useState(1); // 1 or 2 (via collate logic)
  const [printing, setPrinting] = useState(false);
  const [printDone, setPrintDone] = useState(false);
  const [coefficients, setCoefficients] = useState({ color: 0.4, mono: 0.25 });

  // Scan state
  const [scanEmail, setScanEmail] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const [scanConfirm, setScanConfirm] = useState(false); // show confirm step

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      setUserEmail(u.email || "");
      try {
        const snap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", u.uid)));
        if (!snap.empty) {
          const d = snap.docs[0].data();
          setFullName(d.fullname || "Utilisateur");
          setScanEmail(d.email || u.email || "");
        }
      } catch {}
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchCoeff = async () => {
      try {
        const snap = await getDoc(doc(db, "printing", "printers"));
        if (snap.exists()) {
          const d = snap.data();
          setCoefficients({
            mono: d["print black white coefficient"] || 0.25,
            color: d["print colored coefficient"] || 0.4,
          });
        }
      } catch {}
    };
    fetchCoeff();
  }, []);

  // ── File handling ─────────────────────────────────────────
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (selected.type !== "application/pdf") {
      message.error("Veuillez sélectionner un fichier PDF.");
      return;
    }
    setFile(selected);
    setPrintDone(false);
    const reader = new FileReader();
    reader.onload = function () {
      const typedarray = new Uint8Array(this.result);
      pdfjsLib.getDocument(typedarray).promise
        .then(pdf => setNumPages(pdf.numPages))
        .catch(() => setNumPages(null));
    };
    reader.readAsArrayBuffer(selected);
  };

  const pricePerPage = colorMode === "color" ? coefficients.color : coefficients.mono;
  const totalCost = numPages ? (pricePerPage * copies * numPages).toFixed(3) : null;

  // ── Print ─────────────────────────────────────────────────
  const handlePrint = async () => {
    if (!file || !numPages) return message.warning("Veuillez choisir un fichier PDF.");
    if (!fullName) return message.warning("Profil non chargé.");
    setPrinting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("colorMode", colorMode);
      formData.append("copies", copies);
      formData.append("doubleSided", doubleSided);
      formData.append("pagesPerSheet", pagesPerSheet);

      // No manual Content-Type: the browser sets multipart with the correct boundary
      const res = await axios.post(
        `${API_URL}/api/printing/print`,
        formData,
        { headers: await authHeaders() }
      );

      if (!res.data.success) throw new Error(res.data.error || "Erreur");

      await addDoc(collection(db, "buvette"), {
        "Date et Heure": new Date(),
        "Nom Client": fullName,
        Produit: `Impression — ${numPages}p ${colorMode === "color" ? "Couleur" : "N&B"} x${copies}${doubleSided !== "none" ? " R/V" : ""}`,
        Total: parseFloat(totalCost),
        Paiement: false,
        jobId: res.data.jobId,
      });

      setPrintDone(true);
      setFile(null);
      setNumPages(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      message.error(apiError(err, "Erreur lors de l'impression."));
    }
    setPrinting(false);
  };

  // ── Scan ─────────────────────────────────────────────────
  const handleScanConfirm = () => {
    if (!scanEmail.trim() || !scanEmail.includes("@")) {
      message.warning("Veuillez saisir une adresse email valide.");
      return;
    }
    setScanConfirm(true);
  };

  const handleScan = async () => {
    setScanning(true)
    try {
      const res = await axios.post(
        `${API_URL}/api/printing/scan`,
        { email: scanEmail.trim() },
        { headers: await authHeaders() }
      );
      if (!res.data.success) throw new Error(res.data.error);
      setScanDone(true);
      setScanConfirm(false);
    } catch (err) {
      console.error(err);
      message.error(apiError(err, "Erreur lors du scan."));
    }
    setScanning(false);
  };

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Impression & Scan</div>
              <div style={{ fontSize: 11, color: t.text2 }}>{fullName || "Chargement..."}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", margin: "16px 18px 0", background: t.bg3, borderRadius: 12, padding: 4, gap: 4 }}>
          {[
            { id: "print", label: "🖨️ Impression", paid: true },
            { id: "scan",  label: "📠 Scan",       paid: false },
          ].map(tb => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              style={{
                flex: 1, padding: "9px 8px", borderRadius: 9, border: "none",
                background: tab === tb.id ? t.cardBg : "transparent",
                color: tab === tb.id ? t.text1 : t.text3,
                fontSize: 13, fontWeight: tab === tb.id ? 600 : 400,
                cursor: "pointer", transition: "all 0.15s",
                boxShadow: tab === tb.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {tb.label}
              {!tb.paid && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 10, background: t.greenBg, color: t.green }}>
                  Gratuit
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── PRINT TAB ── */}
        {tab === "print" && (
          printDone ? (
            <div style={{ padding: "48px 18px 0", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🖨️</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: t.text1, marginBottom: 8 }}>Impression lancée !</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 24, lineHeight: 1.6 }}>
                Votre document est en cours d'impression.<br />Passez à l'accueil pour régler.
              </div>
              <button onClick={() => setPrintDone(false)} style={{ ...s.primaryBtn, background: t.blue, padding: "10px 28px", width: "auto" }}>
                Nouvelle impression
              </button>
            </div>
          ) : (
            <div style={{ padding: "16px 18px 0", display: "flex", flexDirection: "column", gap: 12 }}>

              {/* File upload */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.text2, marginBottom: 10 }}>📄 Fichier PDF</div>
                <input type="file" accept="application/pdf" ref={fileInputRef} onChange={handleFileChange} style={{ display: "none" }} />
                {!file ? (
                  <button onClick={() => fileInputRef.current?.click()}
                    style={{ width: "100%", border: `2px dashed ${t.border}`, background: t.bg3, borderRadius: 12, padding: "24px 14px", cursor: "pointer", color: t.text2, fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 28 }}>📎</span>
                    <span>Cliquez pour choisir un PDF</span>
                  </button>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: t.bg3, borderRadius: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{file.name}</div>
                      {numPages && <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>{numPages} page{numPages > 1 ? "s" : ""}</div>}
                    </div>
                    <button onClick={() => { setFile(null); setNumPages(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, color: t.red, borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>✕</button>
                  </div>
                )}
              </div>

              {/* Options */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.text2 }}>⚙️ Options d'impression</div>

                {/* Color mode */}
                <div>
                  <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 6 }}>Mode couleur</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[{ val: "mono", label: "⬛ N&B", price: coefficients.mono }, { val: "color", label: "🎨 Couleur", price: coefficients.color }].map(opt => (
                      <button key={opt.val} onClick={() => setColorMode(opt.val)} style={{
                        flex: 1, padding: "9px 8px", borderRadius: 10,
                        border: `1.5px solid ${colorMode === opt.val ? t.blue : t.border}`,
                        background: colorMode === opt.val ? t.blueBg : t.bg3,
                        color: colorMode === opt.val ? t.blueText : t.text2,
                        fontSize: 12, fontWeight: colorMode === opt.val ? 600 : 400, cursor: "pointer",
                      }}>
                        <div>{opt.label}</div>
                        <div style={{ fontSize: 10, marginTop: 2, opacity: 0.75 }}>{opt.price} dt/page</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Double sided */}
                <div>
                  <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 6 }}>Recto / Verso</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[{ val: "none", label: "📄 Recto seul" }, { val: "long", label: "📋 Recto-verso long" }, { val: "short", label: "📋 Recto-Verso court" }].map(opt => (
                      <button key={opt.val} onClick={() => setDoubleSided(opt.val)} style={{
                        flex: 1, padding: "8px 4px", borderRadius: 10, fontSize: 11,
                        border: `1.5px solid ${doubleSided === opt.val ? t.blue : t.border}`,
                        background: doubleSided === opt.val ? t.blueBg : t.bg3,
                        color: doubleSided === opt.val ? t.blueText : t.text2,
                        fontWeight: doubleSided === opt.val ? 600 : 400, cursor: "pointer",
                      }}>{opt.label}</button>
                    ))}
                  </div>
                </div>

                {/* Pages per sheet */}
                <div>
                  <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 6 }}>Pages par feuille</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[{ val: 1, label: "1 page" }, { val: 2, label: "2 pages" }].map(opt => (
                      <button key={opt.val} onClick={() => setPagesPerSheet(opt.val)} style={{
                        flex: 1, padding: "9px 8px", borderRadius: 10,
                        border: `1.5px solid ${pagesPerSheet === opt.val ? t.blue : t.border}`,
                        background: pagesPerSheet === opt.val ? t.blueBg : t.bg3,
                        color: pagesPerSheet === opt.val ? t.blueText : t.text2,
                        fontSize: 12, fontWeight: pagesPerSheet === opt.val ? 600 : 400, cursor: "pointer",
                      }}>{opt.label}</button>
                    ))}
                  </div>
                </div>

                {/* Copies */}
                <div>
                  <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 6 }}>Copies</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button onClick={() => setCopies(c => Math.max(1, c - 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, fontSize: 18, cursor: "pointer" }}>−</button>
                    <span style={{ fontSize: 20, fontWeight: 700, color: t.text1, minWidth: 30, textAlign: "center" }}>{copies}</span>
                    <button onClick={() => setCopies(c => Math.min(MAX_COPIES, c + 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, fontSize: 18, cursor: "pointer" }}>+</button>
                  </div>
                </div>
              </div>

              {/* Cost estimate */}
              {totalCost && (
                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.text2, marginBottom: 10 }}>💰 Estimation</div>
                  {[
                    { label: "Pages", value: `${numPages}` },
                    { label: "Copies", value: `× ${copies}` },
                    { label: "Prix / page", value: `${pricePerPage} dt` },
                    ...(doubleSided !== "none" ? [{ label: "Recto-verso", value: "✓" }] : []),
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: t.text2, marginBottom: 4 }}>
                      <span>{row.label}</span><span>{row.value}</span>
                    </div>
                  ))}
                  <div style={{ height: 1, background: t.border, margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700 }}>
                    <span style={{ color: t.text1 }}>Total estimé</span>
                    <span style={{ color: t.blue }}>{totalCost} dt</span>
                  </div>
                  <div style={{ fontSize: 11, color: t.text3, marginTop: 6 }}>💡 Montant ajouté à votre compte buvette</div>
                </div>
              )}

              <button onClick={handlePrint} disabled={printing || !file || !numPages}
                style={{ ...s.primaryBtn, background: printing || !file ? t.bg3 : t.blue, color: printing || !file ? t.text3 : "#fff", padding: "14px", opacity: !file ? 0.5 : 1 }}>
                {printing
                  ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spin size="small" /> Impression...</span>
                  : "🖨️ Lancer l'impression"}
              </button>
            </div>
          )
        )}

        {/* ── SCAN TAB ── */}
        {tab === "scan" && (
          <div style={{ padding: "16px 18px 0", display: "flex", flexDirection: "column", gap: 12 }}>

            {scanDone ? (
              <div style={{ padding: "32px 0", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: t.text1, marginBottom: 8 }}>Scan envoyé !</div>
                <div style={{ fontSize: 13, color: t.text2, marginBottom: 24, lineHeight: 1.6 }}>
                  Placez votre document sur le scanner.<br />
                  Le scan sera envoyé à <strong>{scanEmail}</strong>
                </div>
                <button onClick={() => { setScanDone(false); setScanConfirm(false); }}
                  style={{ ...s.primaryBtn, background: t.blue, padding: "10px 28px", width: "auto" }}>
                  Nouveau scan
                </button>
              </div>
            ) : scanConfirm ? (
              // Confirmation step
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📠</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: t.text1, marginBottom: 8 }}>Confirmer le scan</div>
                  <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 16 }}>
                    Le document sera scanné et envoyé à :
                  </div>
                  <div style={{ background: t.bg3, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 8 }}>
                    📧 {scanEmail}
                  </div>
                  <div style={{ fontSize: 11, color: t.green, background: t.greenBg, borderRadius: 8, padding: "6px 12px", display: "inline-block" }}>
                    ✓ Scan gratuit
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setScanConfirm(false)}
                    style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg3, color: t.text2, fontSize: 13, cursor: "pointer" }}>
                    ← Modifier
                  </button>
                  <button onClick={handleScan} disabled={scanning}
                    style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: t.blue, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    {scanning
                      ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spin size="small" /> Envoi...</span>
                      : "📠 Lancer le scan"}
                  </button>
                </div>
              </div>
            ) : (
              // Email input step
              <>
                <div style={{ background: t.greenBg, border: `1px solid ${t.green}33`, borderRadius: 14, padding: "12px 16px", display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div style={{ fontSize: 13, color: t.green, fontWeight: 500 }}>
                    Le scan est gratuit — vous recevrez votre document par email.
                  </div>
                </div>

                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 8 }}>
                    📧 Email de destination
                  </label>
                  <input
                    type="email"
                    value={scanEmail}
                    onChange={e => setScanEmail(e.target.value)}
                    placeholder="votre@email.com"
                    style={{
                      width: "100%", height: 44, borderRadius: 10,
                      border: `1px solid ${t.border}`, background: t.bg3,
                      color: t.text1, padding: "0 12px", fontSize: 13,
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: 11, color: t.text3, marginTop: 6 }}>
                    Le scan sera envoyé à cette adresse en PDF.
                  </div>
                </div>

                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.text2, marginBottom: 8 }}>📋 Instructions</div>
                  {[
                    "Saisissez votre email ci-dessus",
                    "Cliquez sur \"Préparer le scan\"",
                    "Placez votre document sur le scanner",
                    "Confirmez le lancement du scan",
                    "Récupérez le PDF dans votre boîte mail",
                  ].map((step, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < 4 ? 8 : 0, alignItems: "flex-start" }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: t.blueBg, color: t.blueText, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                      <span style={{ fontSize: 12, color: t.text2, lineHeight: 1.5 }}>{step}</span>
                    </div>
                  ))}
                </div>

                <button onClick={handleScanConfirm}
                  style={{ ...s.primaryBtn, background: t.blue, color: "#fff", padding: "14px" }}>
                  📠 Préparer le scan
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { width: "100%", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" },
};